import { Box, Button } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { flow, getPropertyValue, find, isNotUndefined, hasPropertyValue, isNotNull, isUndefined } from '@hcr/utils'

import { useDestinationsQuery, useDestinationDetailsQuery, useActivitiesQuery, useIdToken } from '../../../../hooks'
import { DateFormat, Path, TimeFormat } from '../../../../models'
import {
  createActivityNameString,
  createActivityServiceReservationValidityString,
  dateFormatter,
  isDestinationOf,
  to,
} from '../../../../utils'
import { LayoutNavigationBackError500 } from '../../../common'
import { LayoutTicketDetailsActive, LayoutTicketDetailsActiveLoading, TicketValidityActive } from '../common'

interface TicketDetailsActivityActiveProps {
  activityId: string
  resortsIds: number[]
}

const BACK_NAVIGATION_PATH = to(Path.Booking)

export const TicketDetailsActivityActive: FC<TicketDetailsActivityActiveProps> = ({ activityId, resortsIds }) => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const navigate = useNavigate()

  const activity = useActivitiesQuery(
    { idToken: String(idToken), resortsIds },
    {
      select: find(hasPropertyValue('activity_id', activityId)),
      enabled: isNotNull(idToken),
    }
  )

  const destination = useDestinationsQuery({
    select: flow(getPropertyValue('destinations'), find(isDestinationOf(activity.data))),
    enabled: isNotUndefined(activity.data),
  })

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destination.data?.destinationId) },
    { enabled: isNotUndefined(destination.data?.destinationId) }
  )

  if (
    activity.isError ||
    (activity.isSuccess && isUndefined(activity.data)) ||
    destination.isError ||
    (destination.isSuccess && isUndefined(destination.data)) ||
    destinationDetails.isError
  ) {
    return <LayoutNavigationBackError500 to={BACK_NAVIGATION_PATH} />
  }

  if (activity.isSuccess && destination.isSuccess && destinationDetails.isSuccess) {
    if (isNotUndefined(activity.data)) {
      return (
        <LayoutTicketDetailsActive
          additionalInfo={[
            { label: t('tickets.resort'), value: destinationDetails.data.name },
            ...(activity.data.service_reservations.length > 0
              ? [
                  {
                    label: t('tickets.additional-services'),
                    value: activity.data.service_reservations.map(createActivityServiceReservationValidityString),
                  },
                ]
              : []),
          ]}
          backNavigationPath={BACK_NAVIGATION_PATH}
          cta={
            <Button onClick={() => navigate(BACK_NAVIGATION_PATH)} variant='outlined' color='black'>
              {t('tickets.back-to-booking')}
            </Button>
          }
          destinationName={destinationDetails.data.name}
          name={createActivityNameString(activity.data)}
          validity={t('tickets.enjoy-your-visit')}
        >
          <TicketValidityActive>
            <Box>{dateFormatter.format(DateFormat.Standard, activity.data.start_date)}</Box>
            <Box>
              {dateFormatter.formatDateRange({
                from: activity.data.start_date,
                fromFormat: TimeFormat.Standard,
                to: activity.data.end_date,
                toFormat: TimeFormat.Standard,
              })}
            </Box>
          </TicketValidityActive>
        </LayoutTicketDetailsActive>
      )
    }

    return <LayoutNavigationBackError500 to={BACK_NAVIGATION_PATH} />
  }

  return <LayoutTicketDetailsActiveLoading backNavigationPath={BACK_NAVIGATION_PATH} />
}
